import { useCallback, useMemo, useRef, useState } from 'react'
import { Camera, Check, ImagePlus, Loader2, Upload, Users, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fileToDataUrl } from '@/lib/fileToDataUrl'
import {
  DEFAULT_PHOTOS,
  detectGender,
  photosForGender,
  type Gender,
} from '@/lib/personaPhotoLibrary'

interface Props {
  photo?: string
  initials: string
  isBuyer: boolean
  name?: string
  onChange: (dataUrl: string | undefined) => void
}

const MAX_BYTES = 2 * 1024 * 1024

export function PersonaAvatarUploader({ photo, initials, isBuyer, name, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [libraryOpen, setLibraryOpen] = useState(false)

  const detected = useMemo<Gender>(() => detectGender(name), [name])
  const [filter, setFilter] = useState<Gender>(detected)

  const handleFile = useCallback(async (file: File) => {
    setError(null)
    if (!file.type.startsWith('image/')) {
      setError('Not an image')
      return
    }
    if (file.size > MAX_BYTES) {
      setError('Must be < 2MB')
      return
    }
    setIsProcessing(true)
    try {
      const dataUrl = await fileToDataUrl(file)
      onChange(dataUrl)
    } catch {
      setError('Upload failed')
    } finally {
      setIsProcessing(false)
    }
  }, [onChange])

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFile(f)
    e.target.value = ''
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const removePhoto = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(undefined)
  }

  const openLibrary = (e: React.MouseEvent) => {
    e.stopPropagation()
    setFilter(detected)
    setLibraryOpen(true)
  }

  const handlePickLibrary = (url: string) => {
    onChange(url)
    setLibraryOpen(false)
  }

  const visiblePhotos = photosForGender(filter)

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'group relative w-[72px] h-[72px] rounded-full flex items-center justify-center text-white font-display text-[26px] font-bold shadow-lg transition-all cursor-pointer overflow-hidden',
          photo
            ? 'bg-warm-200'
            : isBuyer
              ? 'bg-gradient-to-br from-business-light to-business'
              : 'bg-gradient-to-br from-market-light to-market',
          isDragging && 'ring-2 ring-market ring-offset-2',
        )}
        title={photo ? 'Click to replace photo' : 'Click or drop an image'}
      >
        {photo && (
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {!photo && !isProcessing && initials}
        {isProcessing && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-white" />
          </div>
        )}
        {!isProcessing && (
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            {isDragging ? (
              <Upload className="w-5 h-5 text-white" />
            ) : (
              <Camera className="w-5 h-5 text-white" />
            )}
          </div>
        )}
        {photo && !isProcessing && (
          <button
            onClick={removePhoto}
            className="absolute top-0 right-0 w-5 h-5 rounded-full bg-white border border-warm-300 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-error hover:text-white hover:border-error"
            title="Remove photo"
          >
            <X className="w-3 h-3" />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onInputChange}
          className="hidden"
        />
      </div>

      <button
        onClick={openLibrary}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-text-muted hover:text-market transition-colors"
        title="Pick from library"
        type="button"
      >
        <ImagePlus className="w-3 h-3" />
        Choose from library
      </button>

      {error && <span className="text-[10px] text-error">{error}</span>}

      {libraryOpen && (
        <LibraryModal
          selectedUrl={photo}
          filter={filter}
          onFilterChange={setFilter}
          detected={detected}
          onClose={() => setLibraryOpen(false)}
          onPick={handlePickLibrary}
          visiblePhotos={visiblePhotos}
        />
      )}
    </div>
  )
}

interface LibraryModalProps {
  selectedUrl?: string
  filter: Gender
  onFilterChange: (g: Gender) => void
  detected: Gender
  onClose: () => void
  onPick: (url: string) => void
  visiblePhotos: typeof DEFAULT_PHOTOS
}

function LibraryModal({
  selectedUrl,
  filter,
  onFilterChange,
  detected,
  onClose,
  onPick,
  visiblePhotos,
}: LibraryModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="w-[520px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-warm-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-warm-200">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-market" />
            <h3 className="font-display text-sm font-semibold">Choose a persona photo</h3>
            {detected !== 'unknown' && (
              <span className="text-[10px] text-text-muted">
                · showing {detected === 'female' ? 'women' : 'men'} (from name)
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-muted hover:text-text hover:bg-warm-100 transition-colors"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-5 py-2 border-b border-warm-200/70 text-[11px]">
          <FilterChip label="All" active={filter === 'unknown'} onClick={() => onFilterChange('unknown')} />
          <FilterChip label="Women" active={filter === 'female'} onClick={() => onFilterChange('female')} />
          <FilterChip label="Men" active={filter === 'male'} onClick={() => onFilterChange('male')} />
        </div>

        <div className="p-4 grid grid-cols-5 gap-3 max-h-[400px] overflow-auto">
          {visiblePhotos.map((p) => {
            const isSelected = selectedUrl === p.url
            return (
              <button
                key={p.id}
                onClick={() => onPick(p.url)}
                className={cn(
                  'relative aspect-square rounded-full overflow-hidden border-2 transition-all hover:scale-105',
                  isSelected ? 'border-market ring-2 ring-market/30' : 'border-warm-200 hover:border-market/40',
                )}
                type="button"
                title={p.gender === 'female' ? 'Female portrait' : 'Male portrait'}
              >
                <img src={p.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                {isSelected && (
                  <div className="absolute inset-0 bg-market/20 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-market flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                )}
              </button>
            )
          })}
        </div>

        <div className="px-5 py-3 border-t border-warm-200 bg-warm-50 flex items-center justify-between">
          <p className="text-[11px] text-text-muted">
            Prefer your own? Close this and click the avatar to upload.
          </p>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium rounded-md text-text-secondary hover:bg-warm-100 transition-colors"
            type="button"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={cn(
        'px-2.5 py-1 rounded-full transition-colors',
        active
          ? 'bg-market/10 text-market font-medium'
          : 'text-text-muted hover:text-text-secondary hover:bg-warm-100',
      )}
    >
      {label}
    </button>
  )
}
